import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { listingPath } from "@/lib/constants";
import { Search, Package, Tag, User, X, ShieldCheck } from "lucide-react";
import { RoleTag } from "@/components/admin/RoleTag";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1 font-mono text-[10px] uppercase tracking-[1.5px] text-secondary-foreground">
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </div>
  );
}

// Global admin jump-to search: orders, listings, users by id or @handle.
export function AdminGlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRes(null); setLoading(false); return; }
    setLoading(true);
    const id = setTimeout(() => {
      api.get(`/admin/search?q=${encodeURIComponent(t)}`)
        .then(({ data }) => { setRes(data); setOpen(true); })
        .catch(() => setRes(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };
  const total = res ? (res.users.length + res.listings.length + res.orders.length) : 0;
  const rowCls = "flex items-start gap-2 w-full text-left px-3 py-2 hover:bg-obsidian/60 transition-colors border-t border-sniper/40";

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="admin-global-search">
      <div className="flex items-center gap-2 bg-surface border border-sniper px-3 h-10 focus-within:border-olive transition-colors">
        <Search className="w-4 h-4 text-secondary-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (res) setOpen(true); }}
          placeholder="Search orders, listings, users…"
          className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-secondary-foreground font-mono"
          data-testid="admin-search-input"
        />
        {q && (
          <button onClick={() => { setQ(""); setRes(null); setOpen(false); }} data-testid="admin-search-clear" className="text-secondary-foreground hover:text-foreground" aria-label="Clear search">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-sniper shadow-2xl max-h-[72vh] overflow-y-auto" data-testid="admin-search-results">
          {loading && !res && <div className="px-3 py-3 text-xs font-mono uppercase text-secondary-foreground">Searching…</div>}
          {res && total === 0 && <div className="px-3 py-4 text-xs font-mono uppercase text-secondary-foreground" data-testid="admin-search-empty">No matches for “{q.trim()}”.</div>}

          {res && res.orders.length > 0 && (
            <Section title="Orders" icon={Package}>
              {res.orders.map((o) => (
                <button key={o.order_id} onClick={() => go(`/admin/orders?order=${o.order_id}`)} className={rowCls} data-testid={`search-order-${o.order_id}`}>
                  <span className="font-mono text-xs text-olive-bright shrink-0">{o.order_id}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-secondary-foreground">
                    {o.title || "—"} · {o.type} · {String(o.status || "").replace("_", " ")}
                  </span>
                  <span className="font-mono text-xs text-foreground shrink-0">{money(o.amount_cents)}</span>
                </button>
              ))}
            </Section>
          )}

          {res && res.listings.length > 0 && (
            <Section title="Listings" icon={Tag}>
              {res.listings.map((l) => (
                <button key={l.listing_id} onClick={() => go(listingPath(l))} className={rowCls} data-testid={`search-listing-${l.listing_id}`}>
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{l.title || l.listing_id}</span>
                  <span className="font-mono text-[10px] uppercase text-secondary-foreground shrink-0">{String(l.status || "").replace("_", " ")}</span>
                  {l.price != null && <span className="font-mono text-xs text-foreground shrink-0">${l.price}</span>}
                </button>
              ))}
            </Section>
          )}

          {res && res.users.length > 0 && (
            <Section title="Users" icon={User}>
              {res.users.map((u) => (
                <button key={u.user_id} onClick={() => go(`/u/${u.username}`)} className={rowCls} data-testid={`search-user-${u.username}`}>
                  {["admin", "super_admin"].includes(u.role) ? <ShieldCheck className="w-3.5 h-3.5 text-olive-bright mt-0.5 shrink-0" /> : <User className="w-3.5 h-3.5 text-secondary-foreground mt-0.5 shrink-0" />}
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">@{u.username}{u.name ? <span className="text-secondary-foreground"> · {u.name}</span> : null}</span>
                  <RoleTag kind={u.role} />
                </button>
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminGlobalSearch;
